import { _ } from "../../../polyglot/polyglot.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractImporter from "../AbstractImporter.js";
import AuthenticationStep from "../AuthenticationStep.js";
import DataLoadingOptionStep from "./DataLoadingOptionStep.js";
import session from "../../../session.js";
import XeroIntegration from "../../../../models/integration/XeroIntegration.js";

/**
 * `XeroImporter` Import wizard for the Xero integration.
 */
export default class XeroImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		const antiForgeryToken = session.getAntiForgeryToken();

		this._xeroImportRepository = makeMonitorRepository({
			route: "/Shared/Import/Xero",
			antiForgeryToken,
		});

		this._companyChartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts",
			antiForgeryToken,
		});

		this._router = router.getRouter();
	}

	routeName() {
		return "xero";
	}

	_wizardSteps() {
		return [
			this._createAuthenticationStep(),
			this._createDataLoadingOptionStep(),
		];
	}

	// Step 1: Launch the authentication flow.
	_createAuthenticationStep() {
		return new AuthenticationStep({
			submitCallback: this._launchAuthenticationFlow.bind(this),
		});
	}

	// Step 2: Show the data loading options
	_createDataLoadingOptionStep() {
		return new DataLoadingOptionStep({
			submitCallback: this._dataLoadingOptionsToImportData.bind(this),
			canGoToPreviousStep: false,
		});
	}

	// Step 1 => 2 Authenticate the user if needed or proceed to
	// the next step.
	_launchAuthenticationFlow({ onDone }) {
		const hasAccessToken =
			this._router.getParameter("hasAccessToken") === "true";

		if (hasAccessToken) {
			onDone();
		} else {
			// New integration, redirect to Xero for authentication.
			this._redirectToXero();
		}
	}

	// Redirects to Xero to finish the authentication step.
	_redirectToXero() {
		let query = `${
			window.location.pathname
		}#!/import/${this.routeName()}?hasAccessToken=true`;

		query += `&abortUrl=${this._router.getParameter("abortUrl")}`;
		query += `&returnUrl=${this._router.getParameter("returnUrl")}`;

		if (this._getCompanyId()) {
			query += `&companyId=${this._getCompanyId()}`;
		}
		let redirectUrl = `/Shared/Import/Xero/?agencyId=${this.getAgencyId()}&returnUrl=${encodeURIComponent(
			query,
		)}`;

		const businessIdentityCode =
			this._context.company?._businessIdentityCode;
		if (businessIdentityCode) {
			redirectUrl += `&businessIdentityCode=${businessIdentityCode}`;
		}

		this._router.redirectToLocationPath(redirectUrl);
	}

	// Step 2 => 3: Post the selected data loading options
	async _dataLoadingOptionsToImportData({ onDone, step }) {
		try {
			const [importData, companyChartsOfAccounts] = await Promise.all([
				this._fetchXeroData(),
				this._fetchChartsOfAccounts(),
			]);

			if (importData.hasErrors) {
				step.addSubmitError(this._errorToMessage(importData));
				onDone();
			} else {
				if (companyChartsOfAccounts) {
					importData.chartOfAccounts = [...companyChartsOfAccounts];
				}
				this._context.importData = importData;
				onDone();
			}
		} catch (error) {
			step.addSubmitError(this._errorToMessage(error.responseJSON));
			onDone();
		}
	}

	_getCompanyId() {
		return (
			this._router.getParameter("companyId") || this._context.company?._id
		);
	}

	_errorToMessage(error) {
		switch (error.typeOfError) {
			case "UnknownTenant": {
				return _(
					"Can not identify the newly connected company. Make sure you select a new company.",
				);
			}
			case "RateExceeded": {
				return _("Xero API query rate exceeded");
			}
			case "InternalApiError": {
				return _("Internal Xero API error");
			}
			default: {
				return error.message || error.exceptionMessage;
			}
		}
	}

	_fetchXeroData() {
		return this._xeroImportRepository.save({
			model: {
				agencyId: this.getAgencyId(),
			},
			filter: {
				partialImport: this._context.xeroPartialImport,
			},
			notify: false,
		});
	}

	_fetchChartsOfAccounts() {
		// The company is already imported once
		if (this._getCompanyId()) {
			return this._companyChartOfAccountsRepository.get({
				filter: { companyId: this._getCompanyId() },
			});
		}
		return Promise.resolve();
	}

	_wantExtendedActionsInConfigureImportDataStep() {
		return true;
	}
}

XeroImporter.integrationType = XeroIntegration.getType();
